import {
  IcPeriod, InvestorContributionWithCf,
} from 'src/app/domain/investors-and-net-irr/dto/InvestorContribution';
import { DataType, formats, getCell, PartialCell, withCellStyles } from '../../utils';
import * as XLSX from 'xlsx-js-style';
import { Rows } from '../../interfaces';
import { CellObject, CellStyle } from 'xlsx-js-style';
import {
  NetIrrWrapper,
} from '../../../fund-performance/component/fund-performances/fund-performance-card/fund-performance-card.component';

export const getPreparedData = (data: NetIrrWrapper, headers: string[]): Rows => {
  const newData: Rows = [];
  newData.push(headers);
  const rows = getManyRows(data.icPeriods, headers);
  const stopLen = rows.length;
  newData.push(...rows);
  const row = [];
  const advRow = getAdvanceAndNavRows(data.icPeriods, headers, 0);
  newData.push(...advRow);
  const navRow = getAdvanceAndNavRows(data.icPeriods, headers, 1);
  newData.push(...navRow);
  if (data.isAfterCarry) {
    const interestRow = getAdvanceAndNavRows(data.icPeriods, headers, 2);
    newData.push(...interestRow);
  }
  let irrRow = getAdvanceAndNavRows(data.icPeriods, headers, 3, stopLen);
  newData.push(...irrRow);
  if (data.isAfterCarry) {
    irrRow = getAdvanceAndNavRows(data.icPeriods, headers, 4, stopLen);
    newData.push(...irrRow);
  }

  newData.push(row);
  return newData;
};

export const getManyRows = (icPeriod: IcPeriod[], headers: string[]): Rows => {
  const rows: Rows = [];
  const arr = icPeriod.flatMap((ic) => ic.data).reverse();
  const mappedToHeaders = arr.map((d) => getNewMapping(d));
  for (const mapped of mappedToHeaders) {
    const row: PartialCell[] = [];
    for (const header of headers) {
      if (header === 'Company') {
        row.push(withCellStyles(mapped[header] as DataType, leftBold));
      } else if (mapped) {
        row.push(mapped[header] as Partial<CellObject>);
      }
    }
    if (row.length) {
      rows.push(row);
    }
  }
  return rows;
};

export const getAdvanceAndNavRows = (icPeriod: IcPeriod[], headers: string[], type: number, stopLen: number = null): Rows => {
  const rows: Rows = [];
  let mappedToHeaders: InvestorContributionMapping;
  switch (type) {
    case 0:
      mappedToHeaders = getAdvanceMapping(icPeriod[0]);
      break;
    case 1:
      mappedToHeaders = getNavMapping(icPeriod[0]);
      break;
    case 2:
      mappedToHeaders = getCarryInterestMapping(icPeriod[0]);
      break;
    case 3:
      mappedToHeaders = getIrrBeforeCarryMapping(stopLen);
      break;
    case 4:
      mappedToHeaders = getIrrAfterCarryMapping(stopLen);
      break;
  }

  const row = [];
  for (const header of headers) {
    row.push(withCellStyles(mappedToHeaders[header] as DataType, bold));
  }
  rows.push(row);
  return rows;
};

const getNewMapping = (i: InvestorContributionWithCf): null|InvestorContributionMapping => {
  if ((!i.distribution && !i.contribution)) {
    return null;
  }
  return {
    Code: i.investor.code,
    Investor: i.investor?.name,
    Date: i.period,
    Type: i.contribution ? 'contribution' : 'distribution',
    Contribution: getCell({
      v: (i.contribution ? i.contribution : i.distribution),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  };
};

const getAdvanceMapping = (icPeriod: IcPeriod): InvestorContributionMapping => ({
  Code: '',
  Investor: '',
  Date: icPeriod.fp.period.toString(),
  Type: 'Advance/(Due)',
  Contribution: getCell({
    v: icPeriod.summary.netGainLossDiff,
    z: formats.thousandsSeparator,
    t: 'n',
  }),
});

const getNavMapping = (icPeriod: IcPeriod): InvestorContributionMapping => ({
  Code: '',
  Investor: '',
  Date: icPeriod.fp.period.toString(),
  Type: 'NAV',
  Contribution: getCell({
    v: (icPeriod.summary.beforeCarry.navAndDistribution - icPeriod.summary.beforeCarry.distribution),
    z: formats.thousandsSeparator,
    t: 'n',
  }),
});


const getCarryInterestMapping = (icPeriod: IcPeriod): InvestorContributionMapping => ({
  Code: '',
  Investor: '',
  Date: icPeriod.fp.period.toString(),
  Type: 'Carried interest',
  Contribution: getCell({
    v: icPeriod.summary.carry.unrealized,
    z: formats.thousandsSeparator,
    t: 'n',
  }),
});

const getIrrBeforeCarryMapping = (stopIndex: number): InvestorContributionMapping => ({
  Code: '',
  Investor: '',
  Date: '',
  Type: 'Before carry',
  Contribution: getCell({
    // The eslint rule is disabled intentionally to maintain the formatting required for Excel display.
    // eslint-disable-next-line max-len
    f: `=IF(SUM(E2:E${stopIndex + 3})<0,XIRR(E2:E${stopIndex + 3},C2:C${stopIndex + 3},-10%),XIRR(E2:E${stopIndex + 3},C2:C${stopIndex + 3}))`,
    z: formats.percents, t: 'n',
  }),
});

const getIrrAfterCarryMapping = (stopIndex: number): InvestorContributionMapping => ({
  Code: '',
  Investor: '',
  Date: '',
  Type: 'After carry',
  Contribution: getCell({
    // The eslint rule is disabled intentionally to maintain the formatting required for Excel display.
    // eslint-disable-next-line max-len
    f: `=IF(SUM(E2:E${stopIndex + 4})<0,XIRR(E2:E${stopIndex + 4},C2:C${stopIndex + 4},-10%),XIRR(E2:E${stopIndex + 4},C2:C${stopIndex + 4}))`,
    z: formats.percents, t: 'n',
  }),
});

const leftBold: XLSX.CellStyle = {
  alignment: { horizontal: 'left' },
  font: { bold: true },
};

const bold: CellStyle = {
  font: { bold: true },
};

class InvestorContributionMapping {
  Type: string;
  Investor: string;
  Code: string;
  Date: string;
  Contribution: PartialCell;
}
